import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { getStripe, getStripeEnvironment } from "@/lib/stripe";
import { createCheckoutSession, getPrices } from "@/utils/payments.functions";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { PhotoGuide } from "@/components/protocols/PhotoGuide";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { useT, useDocumentTitleKey, useLocale } from "@/i18n/locale-context";

type Plan =
  | "premium_monthly"
  | "premium_yearly"
  | "protocols_pro_monthly"
  | "protocols_pro_yearly";

const FALLBACK_PRICES = [
  { priceId: "premium_monthly" as const, stripePriceId: "", amount: 499, currency: "eur", interval: "month" as const },
  { priceId: "premium_yearly" as const, stripePriceId: "", amount: 4900, currency: "eur", interval: "year" as const },
  { priceId: "protocols_pro_monthly" as const, stripePriceId: "", amount: 999, currency: "eur", interval: "month" as const },
  { priceId: "protocols_pro_yearly" as const, stripePriceId: "", amount: 8900, currency: "eur", interval: "year" as const },
];

const ALL_PLANS: Plan[] = [
  "premium_monthly",
  "premium_yearly",
  "protocols_pro_monthly",
  "protocols_pro_yearly",
];

export const Route = createFileRoute("/checkout")({
  head: () => ({ meta: [{ title: "Passer Premium — Glow Up Coach" }] }),
  validateSearch: (s: Record<string, unknown>): { plan?: Plan } => {
    const raw = typeof s.plan === "string" ? s.plan : "";
    const plan = (ALL_PLANS as readonly string[]).includes(raw)
      ? (raw as Plan)
      : ("premium_monthly" as Plan);
    return { plan };
  },
  component: CheckoutPage,
});

function CheckoutPage() {
  useDocumentTitleKey("checkout.head.title");
  const t = useT();
  const { locale } = useLocale();
  const nav = useNavigate();
  const { plan: initialPlan } = Route.useSearch();
  const [plan, setPlan] = useState<Plan>(initialPlan ?? "premium_monthly");
  const fetchPrices = useServerFn(getPrices);
  const env = getStripeEnvironment();

  const { data: pricesData, isError } = useQuery({
    queryKey: ["stripe-prices", env],
    queryFn: () => fetchPrices({ data: { environment: env } }),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const prices = useMemo(() => {
    if (isError || !pricesData || pricesData.length === 0) return FALLBACK_PRICES;
    // Ensure both plans are present; fill missing ones from fallback.
    return FALLBACK_PRICES.map(
      (fb) => pricesData.find((p) => p.priceId === fb.priceId) ?? fb,
    );
  }, [pricesData, isError]);

  const formatPrice = (priceId: Plan) => {
    const p = prices.find((x) => x.priceId === priceId);
    if (!p) return { amount: "…", period: "" };
    const amount = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: p.currency.toUpperCase(),
      minimumFractionDigits: p.amount % 100 === 0 ? 0 : 2,
    }).format(p.amount / 100);
    const period =
      p.interval === "month"
        ? t("checkout.plan.monthly.price_period")
        : p.interval === "year"
          ? t("checkout.plan.yearly.price_period")
          : "";
    return { amount, period };
  };

  const isPro = plan === "protocols_pro_monthly" || plan === "protocols_pro_yearly";
  const monthlyKey: Plan = isPro ? "protocols_pro_monthly" : "premium_monthly";
  const yearlyKey: Plan = isPro ? "protocols_pro_yearly" : "premium_yearly";
  const monthly = formatPrice(monthlyKey);
  const yearly = formatPrice(yearlyKey);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav({ to: "/login" });
    });
  }, [nav]);

  const checkoutOptions = useMemo(
    () => ({
      fetchClientSecret: async (): Promise<string> => {
        const selected = prices.find((p) => p.priceId === plan);
        const cs = await createCheckoutSession({
          data: {
            priceId: plan,
            ...(selected?.stripePriceId && { stripePriceId: selected.stripePriceId }),
            environment: getStripeEnvironment(),
            returnUrl: `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`,
            locale,
          },
        });
        if (!cs) throw new Error("No client secret returned");
        return cs;
      },
    }),
    [plan, prices],
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PaymentTestModeBanner />
      <Header />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-display font-bold text-3xl mb-2 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          {t("checkout.title")}
        </h1>
        <p className="text-muted-foreground text-sm mb-6">
          {t("checkout.subtitle")}
        </p>

        <div className="flex gap-2 mb-4 text-xs">
          <button
            type="button"
            onClick={() => setPlan("premium_monthly")}
            className={`rounded-full px-3 py-1.5 border ${!isPro ? "border-foreground bg-foreground/5 font-semibold" : "border-foreground/20 hover:border-foreground/40"}`}
          >
            {t("checkout.tier.premium")}
          </button>
          <button
            type="button"
            onClick={() => setPlan("protocols_pro_monthly")}
            className={`rounded-full px-3 py-1.5 border ${isPro ? "border-foreground bg-foreground/5 font-semibold" : "border-foreground/20 hover:border-foreground/40"}`}
          >
            {t("checkout.tier.pro")}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-8">
          <button
            type="button"
            onClick={() => setPlan(monthlyKey)}
            className={`rounded-2xl border p-4 text-left transition ${plan === monthlyKey ? "border-foreground bg-foreground/5" : "border-foreground/15 hover:border-foreground/40"}`}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">{t("checkout.plan.monthly.label")}</p>
            <p className="font-display font-bold text-xl" style={{ fontFamily: "var(--font-display)" }}>
              {monthly.amount}
              <span className="text-sm font-normal text-muted-foreground">{monthly.period}</span>
              <span className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-widest text-muted-foreground border border-foreground/15 rounded px-1.5 py-0.5">
                {t("checkout.plan.tax_badge")}
              </span>
            </p>
          </button>
          <button
            type="button"
            onClick={() => setPlan(yearlyKey)}
            className={`rounded-2xl border p-4 text-left transition ${plan === yearlyKey ? "border-foreground bg-foreground/5" : "border-foreground/15 hover:border-foreground/40"}`}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-accent mb-1">{t("checkout.plan.yearly.label")}</p>
            <p className="font-display font-bold text-xl" style={{ fontFamily: "var(--font-display)" }}>
              {yearly.amount}
              <span className="text-sm font-normal text-muted-foreground">{yearly.period}</span>
              <span className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-widest text-muted-foreground border border-foreground/15 rounded px-1.5 py-0.5">
                {t("checkout.plan.tax_badge")}
              </span>
            </p>
          </button>
        </div>

        <p className="text-xs text-muted-foreground mb-6 -mt-4">
          {t("checkout.plan.tax_note")}
          {isPro && <span className="ml-1">· {t("checkout.tier.pro.trial_note")}</span>}
        </p>

        <PhotoGuide />

        <div id="checkout" key={plan} className="rounded-2xl overflow-hidden border border-foreground/10">
          <EmbeddedCheckoutProvider stripe={getStripe()} options={checkoutOptions}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    </div>
  );
}
